import type { FormConfig, FormGenieLoaderOptions, FormGenieUsageRecord } from './formgenie-types';

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Strips markdown code fences if the LLM wrapped the JSON in them,
 * then parses and validates the minimum required FormConfig shape.
 *
 * Returns null if the string cannot be parsed or is missing required fields.
 */
export function parseFormConfig(raw: string | null | undefined): FormConfig | null {
  if (!raw) return null;
  try {
    const cleaned = raw
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/```\s*$/i, '')
      .trim();
    const parsed = JSON.parse(cleaned);
    if (!isValidFormConfig(parsed)) return null;
    return parsed as FormConfig;
  } catch {
    return null;
  }
}

/**
 * Type guard — returns true if the value has the minimum structure
 * of a valid FormConfig (formName + sections array).
 */
export function isValidFormConfig(config: unknown): config is FormConfig {
  if (!config || typeof config !== 'object') return false;
  const c = config as Record<string, unknown>;
  return typeof c['formName'] === 'string' && Array.isArray(c['sections']);
}

// ---------------------------------------------------------------------------
// String helpers
// ---------------------------------------------------------------------------

/**
 * Converts camelCase or snake_case to Title Case.
 * e.g. "dealerCode" → "Dealer Code", "repair_order" → "Repair Order"
 */
export function toTitleCase(str: string): string {
  return str
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

// ---------------------------------------------------------------------------
// API fetch — plain fetch, works in any context (public, authenticated, SSR)
// ---------------------------------------------------------------------------

/**
 * Fetches a FormConfig from the API using a usage_id.
 *
 * Works in any context — pass a token for authenticated internal apps,
 * omit it for public/unauthenticated routes.
 *
 * Throws on network error, non-2xx response, or invalid FormConfig.
 *
 * @example
 * // Public (no auth):
 * const config = await fetchFormGenieConfig('abc-123', {
 *   apiBaseUrl: 'https://api.pal.circuitry.ai',
 * });
 *
 * @example
 * // Authenticated internal app:
 * const config = await fetchFormGenieConfig('abc-123', {
 *   apiBaseUrl: 'https://api.internal.circuitry.ai',
 *   token: userToken,
 * });
 */
export async function fetchFormGenieConfig(
  usageId: string,
  options: FormGenieLoaderOptions
): Promise<{ formConfig: FormConfig; record: FormGenieUsageRecord }> {
  const { apiBaseUrl, token } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${apiBaseUrl.replace(/\/$/, '')}/aiworker_usage/${usageId}`;
  const response = await fetch(url, { headers });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Form not found (usage_id: ${usageId})`);
    }
    throw new Error(`Failed to load form: ${response.status} ${response.statusText}`);
  }

  const body = await response.json();

  // Normalise the response — the API may nest data under .data
  const record: FormGenieUsageRecord =
    body?.data ?? body;

  const raw = record?.output_data?.generated_code;
  const formConfig = parseFormConfig(raw);

  if (!formConfig) {
    throw new Error(
      `Form configuration is missing or corrupted (usage_id: ${usageId})`
    );
  }

  return { formConfig, record };
}

// ---------------------------------------------------------------------------
// Submission URL helpers
// ---------------------------------------------------------------------------

/**
 * Constructs the shareable link for a generated form.
 *
 * @param tenantSubdomain - e.g. "acme-motors"
 * @param formName        - URL-safe form name (will be slugified)
 * @param usageId         - the usage_id from FormGenie
 * @param instanceId      - if provided, links to a specific submitted instance (read-only)
 */
export function buildFormGenieUrl(
  tenantSubdomain: string,
  formName: string,
  usageId: string,
  instanceId?: string
): string {
  const slug = slugifyFormName(formName);
  const base = `https://${tenantSubdomain}.pal.circuitry.ai/genie/${slug}/${usageId}`;
  return instanceId ? `${base}/${instanceId}` : base;
}

/**
 * Converts a form name into a URL-safe slug.
 * "Warranty Claim Form" → "warranty-claim-form"
 */
export function slugifyFormName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

/**
 * Builds the PAL shareable link for a FormGenie form.
 *
 * Mirrors the tenant-subdomain detection used in the "Open PAL" header button:
 * - localhost / app / staging  → https://pal.circuitry.ai/genie/{slug}/{usageId}
 * - org tenant                 → https://{org}.pal.circuitry.ai/genie/{slug}/{usageId}
 *
 * Safe to call in SSR/test environments — falls back to the root PAL domain
 * when `window` is not available.
 */
export function getPalFormUrl(formName: string, usageId: string): string {
  const slug = slugifyFormName(formName);

  if (typeof window === 'undefined') {
    return `https://pal.circuitry.ai/genie/${slug}/${usageId}`;
  }

  const orgName = window.location.hostname.replace(/\.circuitry\.ai$/, '');
  const isGenericHost =
    orgName.includes('localhost') || orgName === 'app' || orgName.includes('staging');

  return isGenericHost
    ? `https://pal.circuitry.ai/genie/${slug}/${usageId}`
    : `https://${orgName}.pal.circuitry.ai/genie/${slug}/${usageId}`;
}
